// ===========================================================================
// Cargo Market — lectura del lote de transportistas (Administración).
// ---------------------------------------------------------------------------
// Acepta la plantilla .xlsx, un .csv o lo que se pega desde Excel/Sheets
// (tabuladores). La primera fila con encabezados reconocibles manda; las
// columnas se reconocen sin importar mayúsculas, acentos ni el orden. Cada fila
// se valida con las mismas reglas que el backend (cargoCarrierInviteService)
// para mostrar los errores ANTES de enviar; el backend vuelve a validar.
// Sin dependencias de React: se prueba en carrierImport.test.ts.
// ===========================================================================

import { isValidRfc, normalizeRfc } from "../../../utils/rfc";
import type { CarrierInviteInput } from "../exchange/api";

/** Filas por carga (igual que BATCH_MAX del backend). */
export const BATCH_MAX = 200;

type Field = keyof CarrierInviteInput;

/** Columnas de la plantilla: encabezado, ancho, ejemplo y alias aceptados. */
export const TEMPLATE_COLUMNS: { field: Field; header: string; width: number; example: string; required?: boolean; aliases: string[] }[] = [
  { field: "legalName", header: "Razón social", width: 32, example: "Transportes del Norte SA de CV", required: true, aliases: ["razon social", "empresa", "company", "legal name", "nombre de la empresa"] },
  { field: "tradeName", header: "Nombre comercial", width: 22, example: "TDN", aliases: ["nombre comercial", "trade name", "marca"] },
  { field: "rfc", header: "RFC", width: 16, example: "TNO010101AB1", aliases: ["rfc", "tax id"] },
  { field: "contactName", header: "Contacto", width: 24, example: "Luis Pérez", required: true, aliases: ["contacto", "nombre del contacto", "nombre", "contact", "contact name", "responsable"] },
  { field: "contactEmail", header: "Correo", width: 28, example: "luis@tdn.com.mx", required: true, aliases: ["correo", "email", "e-mail", "correo electronico", "mail"] },
  { field: "contactPhone", header: "Teléfono", width: 18, example: "55 1234 5678", aliases: ["telefono", "celular", "whatsapp", "phone", "tel"] },
  { field: "city", header: "Ciudad", width: 18, example: "Monterrey", aliases: ["ciudad", "city", "municipio"] },
  { field: "state", header: "Estado", width: 18, example: "Nuevo León", aliases: ["estado", "state", "entidad"] },
  { field: "unitsCount", header: "Unidades", width: 12, example: "12", aliases: ["unidades", "numero de unidades", "units", "flota", "tractos"] },
  { field: "notes", header: "Notas", width: 30, example: "Rutas Norte–Bajío", aliases: ["notas", "comentarios", "observaciones", "notes"] },
];

const fold = (v: unknown) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9@.+-]+/g, " ")
    .trim();

const ALIAS: Record<string, Field> = (() => {
  const m: Record<string, Field> = {};
  for (const c of TEMPLATE_COLUMNS) {
    m[fold(c.header)] = c.field;
    for (const a of c.aliases) m[fold(a)] = c.field;
  }
  return m;
})();

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Teléfono al formato del perfil (`+52 5512345678`); `undefined` = inválido. */
export function normalizePhone(raw: unknown): string | null | undefined {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const digits = s.replace(/\D/g, "");
  if (!digits) return null;
  if (s.startsWith("+")) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : undefined;
  if (digits.length === 10) return `+52 ${digits}`;
  if (digits.length === 12 && digits.startsWith("52")) return `+52 ${digits.slice(2)}`;
  return undefined;
}

const clean = (v: unknown, max: number) => String(v ?? "").replace(/\s+/g, " ").trim().slice(0, max) || null;

/** Una fila → datos listos para enviar + errores legibles (mismas reglas que el backend). */
export function validateInvite(raw: Partial<Record<Field, unknown>>): { input: CarrierInviteInput; errors: string[] } {
  const errors: string[] = [];
  const legalName = clean(raw.legalName, 160);
  const contactName = clean(raw.contactName, 120);
  const contactEmail = clean(raw.contactEmail, 160)?.toLowerCase() ?? null;
  const rfcRaw = clean(raw.rfc, 20);
  const rfc = rfcRaw ? normalizeRfc(rfcRaw) : null;
  const phone = normalizePhone(raw.contactPhone);
  const unitsRaw = clean(raw.unitsCount, 10);
  const units = unitsRaw === null ? null : Number(unitsRaw.replace(/[,\s]/g, ""));

  if (!legalName) errors.push("Falta la razón social");
  if (!contactName) errors.push("Falta el contacto");
  if (!contactEmail) errors.push("Falta el correo");
  else if (!EMAIL_RE.test(contactEmail)) errors.push("Correo inválido");
  if (rfc && !isValidRfc(rfc)) errors.push("RFC inválido");
  if (phone === undefined) errors.push("Teléfono inválido");
  if (units !== null && !(Number.isInteger(units) && units >= 0 && units <= 100000)) errors.push("Unidades inválidas");

  return {
    errors,
    input: {
      legalName: legalName ?? "",
      tradeName: clean(raw.tradeName, 120),
      rfc,
      contactName: contactName ?? "",
      contactEmail: contactEmail ?? "",
      contactPhone: phone ?? null,
      city: clean(raw.city, 80),
      state: clean(raw.state, 80),
      unitsCount: units !== null && Number.isInteger(units) ? units : null,
      notes: clean(raw.notes, 500),
    },
  };
}

export interface ParsedRow {
  /** Renglón en el archivo (1 = encabezados). */
  line: number;
  input: CarrierInviteInput;
  errors: string[];
}

export interface ParsedBatch {
  rows: ParsedRow[];
  /** Columnas reconocidas y las que no (para avisar). */
  recognized: Field[];
  ignored: string[];
  /** Problema general (sin encabezados, sin filas, demasiadas filas…). */
  error: string | null;
}

/**
 * Filas de celdas → lote. La primera fila que trae al menos razón social y
 * correo es la de encabezados. Filas vacías se ignoran; correos repetidos en
 * el mismo lote se marcan (el backend también lo rechaza).
 */
export function parseMatrix(matrix: unknown[][]): ParsedBatch {
  const empty = (r: unknown[]) => r.every((c) => String(c ?? "").trim() === "");
  const headerAt = matrix.findIndex((r) => {
    const fields = r.map((c) => ALIAS[fold(c)]);
    return fields.includes("legalName") && fields.includes("contactEmail");
  });
  if (headerAt < 0) {
    return { rows: [], recognized: [], ignored: [], error: "No encontramos los encabezados. Usa la plantilla: al menos «Razón social», «Contacto» y «Correo»." };
  }
  const header = matrix[headerAt].map((c) => ALIAS[fold(c)] ?? null);
  const recognized = header.filter((f): f is Field => Boolean(f));
  const ignored = matrix[headerAt].filter((c, i) => !header[i] && String(c ?? "").trim()).map((c) => String(c).trim());

  const rows: ParsedRow[] = [];
  const seen = new Map<string, number>();
  for (let i = headerAt + 1; i < matrix.length; i++) {
    const r = matrix[i] || [];
    if (empty(r)) continue;
    const raw: Partial<Record<Field, unknown>> = {};
    header.forEach((f, col) => {
      if (f && raw[f] === undefined) raw[f] = r[col];
    });
    const { input, errors } = validateInvite(raw);
    const line = i + 1;
    if (input.contactEmail && !errors.includes("Correo inválido")) {
      const first = seen.get(input.contactEmail);
      if (first) errors.push(`Correo repetido (renglón ${first})`);
      else seen.set(input.contactEmail, line);
    }
    rows.push({ line, input, errors });
  }
  if (rows.length === 0) return { rows, recognized, ignored, error: "El archivo no trae transportistas debajo de los encabezados." };
  if (rows.length > BATCH_MAX) return { rows, recognized, ignored, error: `Son ${rows.length} transportistas: el máximo por carga es ${BATCH_MAX}. Divide el archivo.` };
  return { rows, recognized, ignored, error: null };
}

/**
 * Texto CSV o pegado desde Excel → celdas. Detecta el separador (tabulador,
 * punto y coma o coma) en la primera línea y respeta comillas dobles.
 */
export function parseDelimited(text: string): string[][] {
  const src = text.replace(/^﻿/, "").replace(/\r\n?/g, "\n");
  const firstLine = src.split("\n", 1)[0] ?? "";
  const counts = { "\t": firstLine.split("\t").length, ";": firstLine.split(";").length, ",": firstLine.split(",").length };
  const sep = (Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? ",") as "\t" | ";" | ",";

  const out: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === sep) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      out.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    out.push(row);
  }
  return out;
}

/** Lee el archivo (.xlsx con exceljs en carga diferida; .csv/.txt como texto). */
export async function readBatchFile(file: File): Promise<ParsedBatch> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xlsx")) {
    const { readXlsxRows } = await import("../../expenses/lib/statementFile");
    return parseMatrix(await readXlsxRows(file));
  }
  if (name.endsWith(".csv") || name.endsWith(".txt")) {
    const buffer = await file.arrayBuffer();
    let text: string;
    try {
      text = new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    } catch {
      text = new TextDecoder("windows-1252").decode(buffer);
    }
    return parseMatrix(parseDelimited(text));
  }
  return { rows: [], recognized: [], ignored: [], error: "Sube un archivo .xlsx o .csv (o pega las filas desde Excel)." };
}

/** Descarga la plantilla .xlsx (generada en el navegador: siempre coincide con lo que se lee). */
export async function downloadTemplate(): Promise<void> {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Transportistas");
  ws.columns = TEMPLATE_COLUMNS.map((c) => ({ header: c.header, key: c.field, width: c.width }));
  ws.addRow(Object.fromEntries(TEMPLATE_COLUMNS.map((c) => [c.field, c.example])));
  const head = ws.getRow(1);
  head.font = { bold: true };
  TEMPLATE_COLUMNS.forEach((c, i) => {
    if (c.required) head.getCell(i + 1).value = `${c.header} *`;
  });
  const buffer = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "plantilla-transportistas-cargo-market.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}
